import {Theme} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useState} from 'react';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {Page} from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import SearchBar from '../../../components/SearchBar';
import {OPERATOR, WhereConstraints} from '../../../graphql/common.datatypes';
import getRoomParties from './getRoomParties.api';
import Results from './Results';
import SearchFilter, {FilterValues} from './SearchFilter';
import getRoomPartiesInMyGroup from './getRoomPartiesInMyGroup.api';
import getMyRoomParties from './getMyRoomParties.api';
import {formatGraphQlDate} from '../../../utils/formatters';
import moment from 'moment';
import {RoomParty} from '../../../graphql/groups.datatypes';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    results: {
        marginTop: theme.spacing(3)
    }
}));

export const initialFilters: FilterValues = {
    start_date: '',
    end_date: '',
    notes: '',
    wheelchair_accessTrue: false,
    wheelchair_accessFalse: false,
    has_infantsTrue: false,
    has_infantsFalse: false,
    disabledTrue: false,
    disabledFalse: true,
    members_different_datesTrue: false,
    members_different_datesFalse: false,
};


export const RoomPartyListContent = (props: { groupId?: number, bookingId?: number, groupRef?: string, isAdmin: boolean, canEdit: boolean,
    selectedRoomParty?: RoomParty,
    setSelectedRoomParty?: (party: RoomParty) => void, }) => {
    const classes = useStyles();
    const {groupId, bookingId, groupRef, isAdmin, canEdit, selectedRoomParty, setSelectedRoomParty} = props;
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [page, setPage] = useState(1);
    const [orderBy] = useState([{column: 'created_at', order: 'DESC'}]);
    const hasGroupBooking = groupId ? {
        column: 'GROUP_ID',
        operator: OPERATOR.EQ,
        value: groupId
    } : undefined;

    const addWhereBookingId = (constraints: WhereConstraints) => {
        const whereConstraints = {...constraints}
        if (bookingId) {
            whereConstraints.AND = [
                {column: 'group_booking_id', operator: OPERATOR.EQ, value: bookingId},
            ]
        }

        return whereConstraints;
    }

    const [filters, setFilters] = useState(initialFilters);

    const applyFilters = (newFilters: FilterValues) => {
        setFilters(newFilters);
        setWhere(getConstraintsFromFilters(newFilters));
    };

    const getConstraintsFromFilters = (newFilters: FilterValues) => {
        let whereConstraints: WhereConstraints = {
            AND: [],
        };

        if (newFilters.notes.length > 0) whereConstraints.AND!.push({
            column: 'notes',
            operator: OPERATOR.LIKE,
            value: `%${newFilters.notes}%`
        });

        if (newFilters.start_date) whereConstraints.AND!.push({
            column: 'start_date',
            operator: OPERATOR.GTE,
            value: newFilters.start_date
        });
        if (newFilters.end_date) whereConstraints.AND!.push({
            column: 'end_date',
            operator: OPERATOR.LTE,
            value: newFilters.end_date
        });

        if (newFilters.wheelchair_accessTrue) whereConstraints.AND!.push({
            column: 'wheelchair_access',
            operator: OPERATOR.EQ,
            value: true
        });
        if (newFilters.wheelchair_accessFalse) whereConstraints.AND!.push({
            column: 'wheelchair_access',
            operator: OPERATOR.EQ,
            value: false
        });

        if (newFilters.has_infantsTrue) whereConstraints.AND!.push({
          column: 'has_infants',
            operator: OPERATOR.EQ,
            value: true
        });
        if (newFilters.has_infantsFalse) whereConstraints.AND!.push({
            column: 'has_infants',
            operator: OPERATOR.EQ,
            value: false
        });
        if (newFilters.disabledTrue) whereConstraints.AND!.push({
            column: 'disabled',
            operator: OPERATOR.EQ,
            value: true
        });
        if (newFilters.disabledFalse) whereConstraints.AND!.push({
            column: 'disabled',
            operator: OPERATOR.EQ,
            value: false
        });
        if (newFilters.members_different_datesTrue) whereConstraints.AND!.push({
            column: 'members_different_dates',
            operator: OPERATOR.EQ,
            value: true
        });
        if (newFilters.members_different_datesFalse) whereConstraints.AND!.push({
            column: 'members_different_dates',
            operator: OPERATOR.EQ,
            value: false
        });

        if (!isAdmin) {
            const threeYears = formatGraphQlDate(moment().subtract((365 * 3), 'days'));
            whereConstraints.AND!.push({
                column: 'end_date',
                operator: OPERATOR.GTE,
                value: threeYears
            });
        }

        whereConstraints = addWhereBookingId(whereConstraints)
        return whereConstraints;
    };

    const [where, setWhere] = useState(getConstraintsFromFilters(initialFilters));
    const roomPartiesData = isAdmin ? getRoomParties(rowsPerPage, page, where, orderBy, hasGroupBooking) :
        groupRef ? getRoomPartiesInMyGroup(groupRef!, rowsPerPage, page, where, orderBy, hasGroupBooking) :
            getMyRoomParties(rowsPerPage, page, where, orderBy, hasGroupBooking);
    let roomParties;
    if (roomPartiesData.data && roomPartiesData.data.roomParties) roomParties = roomPartiesData.data.roomParties;
    if (roomPartiesData.data && roomPartiesData.data.myRoomParties) roomParties = roomPartiesData.data.myRoomParties;
    if (roomPartiesData.data && roomPartiesData.data.roomPartiesInMyGroup) roomParties = roomPartiesData.data.roomPartiesInMyGroup;

    return (
        <React.Fragment>
            {isAdmin && (
                <SearchBar filterContent={
                    <SearchFilter filters={filters} initialFilters={initialFilters} applyFilters={applyFilters}/>
                }
                />
            )}
            <LoadingOverlay loading={roomPartiesData.loading}>
                {roomParties && (
                    <Results
                        className={classes.results}
                        roomParties={roomParties.data}
                        paginatorInfo={roomParties.paginatorInfo}
                        setPage={setPage}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        isAdmin={isAdmin}
                        canEdit={canEdit}
                        groupRef={groupRef}
                        selectedRoomParty={selectedRoomParty}
                        setSelectedRoomParty={setSelectedRoomParty}
                    />
                )}
            </LoadingOverlay>

        </React.Fragment>
    );
};

const RoomPartyList = () => {
    const classes = useStyles();
    const title = 'Room Parties';

    return (
        <Page className={classes.root} title={title}>
            <PageHeader section="groups" title={title}/>
            <RoomPartyListContent canEdit={true} isAdmin={true}/>
        </Page>
    );
};

export default RoomPartyList;
